// #genai Phase 2 — adapts a GET /v1/payment-methods row into the view-model the saved-methods
// row/list components render. The API contract (see MYEV_UI_INTEGRATION_GUIDE.md §2.3) uses
// type "credit-card" | "ach", numeric id, expMonth/expYear, and a nested billingAddress object;
// the presentation components use type "card" | "ach", a formatted "MM/YY" expiration, flattened
// billing-address lines, and derive isExpired client-side. Keeping this mapping isolated means the
// pixel-matched row markup does not have to change.

import { ACH_ACCOUNT_TYPE_IDS, ACH_CORPORATE_CHECKING_TYPE } from 'commonComponents/paymentInformation/PaymentInformationConstants';

// #genai Single source of truth mapping the ACH account-type code (server-authoritative — comes
// back on the /v1/bank-account-info save response and on each /v1/payment-methods row) to its
// display i18n key. Used by the saved-methods list (remove dialog) AND the post-save success
// banner (Figma 331:27171 shows "Corporate Checking Account ending in 6789 has been added.").
export const ACH_ACCOUNT_TYPE_LABEL_KEYS = {
  [ACH_CORPORATE_CHECKING_TYPE]: 'billing_information__pm_corporate_checking_account',
  [ACH_ACCOUNT_TYPE_IDS.Checking]: 'billing_information__pm_checking_account',
  [ACH_ACCOUNT_TYPE_IDS.Savings]: 'billing_information__pm_savings_account',
};

export const getAchAccountTypeLabelKey = (accountType) => (
  ACH_ACCOUNT_TYPE_LABEL_KEYS[accountType] || 'billing_information__pm_checking_account'
);

// #genai Format CyberSource expMonth (1-12) + expYear (4-digit) into the "MM/YY" the row shows.
const formatExpiration = (expMonth, expYear) => {
  if (!expMonth || !expYear) return '';
  const mm = String(expMonth).padStart(2, '0');
  const yy = String(expYear).slice(-2);
  return `${mm}/${yy}`;
};

// #genai A card is expired once its exp month/year is strictly before the current month/year.
const computeIsExpired = (expMonth, expYear) => {
  if (!expMonth || !expYear) return false;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  return expYear < currentYear || (expYear === currentYear && expMonth < currentMonth);
};

// #genai billingAddress is always present (empty strings, never null — see guide §2.3), so we can
// build the address lines without null guards: line 1 = address1 (+ address2), line 2 = "City, ST zip".
const buildAddressLines = (billingAddress = {}) => {
  const lines = [];
  const line1 = [billingAddress.address1, billingAddress.address2].filter(Boolean).join(', ');
  if (line1) lines.push(line1);
  const cityStateZip = [
    [billingAddress.city, billingAddress.state].filter(Boolean).join(', '),
    billingAddress.zip,
  ].filter(Boolean).join(' ');
  if (cityStateZip) lines.push(cityStateZip);
  return lines;
};

export const mapApiPaymentMethodToViewModel = (apiRow) => {
  const isAch = apiRow.type === 'ach';
  const billingAddress = apiRow.billingAddress || {};
  return {
    // #genai apiType + id are what the default/remove endpoints need (:type/:id path params).
    apiType: apiRow.type,
    id: apiRow.id,
    type: isAch ? 'ach' : 'card',
    lastFour: apiRow.lastFour || '',
    isDefault: Boolean(apiRow.isDefault),
    brand: (apiRow.brand || '').toLowerCase(),
    expiration: isAch ? '' : formatExpiration(apiRow.expMonth, apiRow.expYear),
    isExpired: isAch ? false : computeIsExpired(apiRow.expMonth, apiRow.expYear),
    accountType: apiRow.accountType || '',
    nameOnAccount: apiRow.nameOnAccount || '',
    // #genai Company (guide §2.3): there is NO top-level companyName on a row anymore — the company
    // / legal-entity line lives on billingAddress.company for BOTH cards and ACH.
    company: billingAddress.company || '',
    billingAddressLines: buildAddressLines(billingAddress),
    country: billingAddress.country || '',
    billingEmail: billingAddress.email || '',
    phone: billingAddress.phone || '',
    // #genai SLN-5064: phone ext + secondary phone now round-trip on billingAddress (ecomm-orch PR
    // #372 / ev-report-orders PR #220). secondaryPhone was previously hardcoded to '' (always '--').
    phoneExt: billingAddress.phoneExt || '',
    secondaryPhone: billingAddress.secondaryPhone || '',
    secondaryExt: billingAddress.secondaryExt || '',
  };
};

export default mapApiPaymentMethodToViewModel;
